import React, { createContext, useContext, useState, useEffect } from 'react';

// Roles e permissões
export const ROLES = {
  ADM_MASTER: 'adm_master',
  GESTOR: 'gestor',
  TECNICO: 'tecnico',
  EDITOR: 'editor',
  ANALISTA: 'analista',
  AUDITOR: 'auditor',
};

export const PERMISSIONS = {
  // Dashboard
  VIEW_DASHBOARD: 'view_dashboard',

  // Negócios
  VIEW_NEGOCIOS: 'view_negocios',
  CREATE_NEGOCIO: 'create_negocio',
  EDIT_NEGOCIO: 'edit_negocio',
  DELETE_NEGOCIO: 'delete_negocio',
  APPROVE_NEGOCIO: 'approve_negocio',
  REJECT_NEGOCIO: 'reject_negocio',
  SUSPEND_NEGOCIO: 'suspend_negocio',

  // CMS
  VIEW_CMS: 'view_cms',
  CREATE_NOTICIA: 'create_noticia',
  EDIT_NOTICIA: 'edit_noticia',
  PUBLISH_NOTICIA: 'publish_noticia',
  DELETE_NOTICIA: 'delete_noticia',
  EDIT_PAGINAS: 'edit_paginas',

  // Relatórios
  VIEW_RELATORIOS: 'view_relatorios',
  EXPORT_DATA: 'export_data',

  // Governança
  VIEW_AUDITORIA: 'view_auditoria',

  // Usuários
  VIEW_USUARIOS: 'view_usuarios',
  MANAGE_USUARIOS: 'manage_usuarios',

  // Coletivos
  VIEW_COLETIVOS: 'view_coletivos',
  CREATE_COLETIVO: 'create_coletivo',
  EDIT_COLETIVO: 'edit_coletivo',
  APPROVE_COLETIVO: 'approve_coletivo',
  REJECT_COLETIVO: 'reject_coletivo',
  DELETE_COLETIVO: 'delete_coletivo',
  MANAGE_COLETIVO_MEMBROS: 'manage_coletivo_membros',

  // Associações / Org. Sociais
  VIEW_ASSOCIACOES: 'view_associacoes',
  CREATE_ASSOCIACAO: 'create_associacao',
  EDIT_ASSOCIACAO: 'edit_associacao',
  APPROVE_ASSOCIACAO: 'approve_associacao',
  REJECT_ASSOCIACAO: 'reject_associacao',
  DELETE_ASSOCIACAO: 'delete_associacao',
  MANAGE_ASSOCIACAO_PROJETOS: 'manage_associacao_projetos',
};

const ROLE_PERMISSIONS = {
  [ROLES.ADM_MASTER]: Object.values(PERMISSIONS), // Tudo (acesso global)
  [ROLES.GESTOR]: Object.values(PERMISSIONS), // Tudo (dentro do município)
  [ROLES.TECNICO]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_NEGOCIOS,
    PERMISSIONS.CREATE_NEGOCIO,
    PERMISSIONS.EDIT_NEGOCIO,
    PERMISSIONS.APPROVE_NEGOCIO,
    PERMISSIONS.REJECT_NEGOCIO,
    PERMISSIONS.SUSPEND_NEGOCIO,
    PERMISSIONS.VIEW_CMS,
    PERMISSIONS.CREATE_NOTICIA,
    PERMISSIONS.EDIT_NOTICIA,
    PERMISSIONS.PUBLISH_NOTICIA,
    PERMISSIONS.VIEW_RELATORIOS,
    PERMISSIONS.VIEW_USUARIOS,
  ],
  // Editor: revisão e aprovação de anunciantes/anúncios + gestão de conteúdo,
  // categorias e hashtags. NÃO pode deletar negócios (apenas inativar/suspender)
  // e NÃO pode convidar outros admins (apenas empreendedores via "criar negócio").
  [ROLES.EDITOR]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_NEGOCIOS,
    PERMISSIONS.CREATE_NEGOCIO,
    PERMISSIONS.EDIT_NEGOCIO,
    PERMISSIONS.APPROVE_NEGOCIO,
    PERMISSIONS.REJECT_NEGOCIO,
    PERMISSIONS.SUSPEND_NEGOCIO,
    PERMISSIONS.VIEW_CMS,
    PERMISSIONS.CREATE_NOTICIA,
    PERMISSIONS.EDIT_NOTICIA,
    PERMISSIONS.PUBLISH_NOTICIA,
    PERMISSIONS.VIEW_USUARIOS,
  ],
  [ROLES.ANALISTA]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_NEGOCIOS,
    PERMISSIONS.VIEW_CMS,
    PERMISSIONS.VIEW_RELATORIOS,
    PERMISSIONS.EXPORT_DATA,
  ],
  [ROLES.AUDITOR]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_NEGOCIOS,
    PERMISSIONS.VIEW_CMS,
    PERMISSIONS.VIEW_RELATORIOS,
    PERMISSIONS.VIEW_AUDITORIA,
    PERMISSIONS.VIEW_USUARIOS,
  ],
};

const AdminContext = createContext(null);

// Helper to decode JWT token
function decodeToken(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

export function AdminProvider({ children }) {
  // Get user from stored token
  const getStoredUser = () => {
    // Priority 1: explicit adminUser object stored at login (has correct role from backend)
    const storedUser = localStorage.getItem('adminUser') || localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        if (user && user.role) {
          return {
            id: user.id,
            email: user.email,
            nome: user.nome,
            role: user.role,
            municipio_id: user.municipio_id || null,
            avatar: user.picture || user.avatar || null,
          };
        }
      } catch (e) { /* ignore */ }
    }
    // Priority 2: JWT token (may lack role claim → fall back to 'gestor')
    const token = localStorage.getItem('adminToken') || localStorage.getItem('admin_token') || localStorage.getItem('token');
    if (token) {
      const decoded = decodeToken(token);
      if (decoded) {
        return {
          id: decoded.user_id || decoded.sub,
          email: decoded.sub,
          nome: decoded.nome || 'Admin',
          role: decoded.role || 'gestor',
          municipio_id: decoded.municipio_id || null,
        };
      }
    }
    return null;
  };

  // Estado do usuário admin — nunca inicializar com usuário falso; tela
  // aguarda /auth/me quando token existe.
  const [adminUser, setAdminUser] = useState(() => getStoredUser());

  // Fetch full user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      // Prefer canonical token; fall back to legacy admin keys.
      const token = localStorage.getItem('token')
        || localStorage.getItem('adminToken')
        || localStorage.getItem('admin_token');
      if (token) {
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const userData = await response.json();
            const next = {
              id: userData.id,
              nome: userData.nome,
              email: userData.email,
              role: userData.role || 'gestor',
              municipio_id: userData.municipio_id,
              avatar: userData.picture,
            };
            setAdminUser(prev => ({ ...(prev || {}), ...next }));
            // Keep localStorage in sync so subsequent loads pick up correct role
            try {
              const existing = JSON.parse(localStorage.getItem('adminUser') || '{}');
              localStorage.setItem('adminUser', JSON.stringify({ ...existing, ...next }));
            } catch (e) { /* ignore */ }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    };
    fetchUserData();
  }, []);

  // Filtros globais
  const [filtros, setFiltros] = useState({
    dataInicio: null,
    dataFim: null,
    bairroId: null,
    categoriaId: null,
  });

  // Loading state
  const [loading, setLoading] = useState(false);

  // Verificar permissão
  const hasPermission = (permission) => {
    if (!adminUser) return false;
    // adm_master has all permissions
    if (adminUser.role === 'adm_master') return true;
    const userPermissions = ROLE_PERMISSIONS[adminUser.role] || [];
    return userPermissions.includes(permission);
  };

  // Verificar múltiplas permissões (OR)
  const hasAnyPermission = (permissions) => {
    return permissions.some(p => hasPermission(p));
  };

  // Trocar role (para teste)
  const switchRole = (role) => {
    setAdminUser(prev => (prev ? { ...prev, role } : prev));
  };

  const value = {
    // User
    adminUser,
    switchRole,

    // Permissions
    hasPermission,
    hasAnyPermission,
    ROLES,
    PERMISSIONS,

    // Filters
    filtros,
    setFiltros,

    // Loading
    loading,
    setLoading,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
}
